import * as XLSX from 'xlsx';
import { SessionCallEntity, SessionCallStatus, SessionState } from '../types';
import { format } from 'date-fns';

const getStatusLabel = (status: SessionCallStatus): string => {
  switch (status) {
    case SessionCallStatus.New: return 'New';
    case SessionCallStatus.Confirmed: return 'Confirmed';
    case SessionCallStatus.Canceled: return 'Canceled';
    case SessionCallStatus.Modified: return 'Modified';
    case SessionCallStatus.NeedToCall: return 'Need To Call';
    case SessionCallStatus.Failed: return 'Failed';
    default: return 'Unknown';
  }
};

const getStateLabel = (state: SessionState): string => {
  switch (state) {
    case SessionState.Pending: return 'Pending';
    case SessionState.InProgress: return 'In Progress';
    case SessionState.Done: return 'Done';
    case SessionState.Canceled: return 'Canceled';
    case SessionState.Failed: return 'Failed';
    default: return 'Unknown';
  }
};

interface ExcelRow {
  'ID': number;
  'State': string;
  'Status': string;
  'Date of Service': string;
  'Doctor Name': string;
  'Patient Name': string;
  'Patient Last Name': string;
  'Patient Phone': string;
  'Patient Address': string;
  'Destination': string;
  'Destination Info': string;
  'Appointment Time': string;
  'Estimated Duration': string;
  'Extra Notes': string;
  'Conversations': string;
  'Summary': string;
  'Session ID': number;
  'Created At': string;
  'Updated At': string;
}

export const exportSessionCallsToExcel = (
  sessionCalls: SessionCallEntity[], 
  filename: string = 'session-calls'
) => {
  try {
    const excelData: ExcelRow[] = sessionCalls.map(call => ({
      'ID': call.id,
      'State': getStateLabel(call.state),
      'Status': getStatusLabel(call.status),
      'Date of Service': call.dos,
      'Doctor Name': call.drName,
      'Patient Name': call.patientName,
      'Patient Last Name': call.patientLastName,
      'Patient Phone': call.patientPhone,
      'Patient Address': call.patientAddress,
      'Destination': call.destination,
      'Destination Info': call.destinationInfo,
      'Appointment Time': format(new Date(call.appointmentTime), 'MMM dd, yyyy HH:mm:ss'),
      'Estimated Duration': call.estimatedDuration || '',
      'Extra Notes': call.extraNotes || '',
      'Conversations': call.conversations || '',
      'Summary': call.summary || '',
      'Session ID': call.sessionId,
      'Created At': call.createdAt ? format(new Date(call.createdAt), 'MMM dd, yyyy HH:mm:ss') : 'N/A',
      'Updated At': call.updatedAt ? format(new Date(call.updatedAt), 'MMM dd, yyyy HH:mm:ss') : 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    const colWidths = [
      { wch: 8 },   // ID
      { wch: 12 },  // State
      { wch: 12 },  // Status
      { wch: 12 },  // Date of Service
      { wch: 20 },  // Doctor Name
      { wch: 20 },  // Patient Name
      { wch: 20 },  // Patient Last Name
      { wch: 15 },  // Patient Phone
      { wch: 30 },  // Patient Address
      { wch: 20 },  // Destination
      { wch: 25 },  // Destination Info
      { wch: 20 },  // Appointment Time
      { wch: 15 },  // Estimated Duration
      { wch: 30 },  // Extra Notes
      { wch: 40 },  // Conversations
      { wch: 40 },  // Summary
      { wch: 10 },  // Session ID
      { wch: 20 },  // Created At
      { wch: 20 }   // Updated At
    ];
    
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Session Calls');
    
    const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
    const finalFilename = `${filename}_${timestamp}.xlsx`;
    
    XLSX.writeFile(workbook, finalFilename);
    
    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return false;
  }
};